import type { Map as MapboxMap } from 'mapbox-gl';

export class RouteSystem {
  private map: MapboxMap;
  private routeIds = new Set<string>();

  constructor(map: MapboxMap) {
    this.map = map;
  }

  addRoute(id: string, coordinates: [number, number][], color = '#00b8a9'): void {
    const sourceId = `route-${id}`;
    const layerId = `route-line-${id}`;
    const doneSourceId = `route-done-${id}`;
    const doneLayerId = `route-done-line-${id}`;

    // Full route line
    this.map.addSource(sourceId, {
      type: 'geojson',
      data: { type: 'Feature', geometry: { type: 'LineString', coordinates }, properties: {} },
    });
    this.map.addLayer({
      id: layerId,
      type: 'line',
      source: sourceId,
      paint: { 'line-color': color, 'line-width': 4, 'line-opacity': 0.7 },
    }, 'three-layer');

    // Progress line (dashed, dimmer)
    this.map.addSource(doneSourceId, {
      type: 'geojson',
      data: { type: 'Feature', geometry: { type: 'LineString', coordinates: [coordinates[0], coordinates[0]] }, properties: {} },
    });
    this.map.addLayer({
      id: doneLayerId,
      type: 'line',
      source: doneSourceId,
      paint: { 'line-color': color, 'line-width': 3, 'line-opacity': 0.3, 'line-dasharray': [2, 2] },
    }, 'three-layer');

    this.routeIds.add(id);
  }

  updateProgress(id: string, route: [number, number][], progress: number, currentPos: [number, number]): void {
    const doneSourceId = `route-done-${id}`;
    const source = this.map.getSource(doneSourceId) as any;
    if (!source) return;

    const totalSegments = route.length - 1;
    const currentIdx = Math.min(Math.floor(progress * totalSegments) + 1, route.length);
    const completedCoords = [...route.slice(0, currentIdx), currentPos];

    source.setData({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: completedCoords },
      properties: {},
    });
  }

  removeRoute(id: string): void {
    const sourceId = `route-${id}`;
    const layerId = `route-line-${id}`;
    const doneSourceId = `route-done-${id}`;
    const doneLayerId = `route-done-line-${id}`;

    if (this.map.getLayer(layerId)) this.map.removeLayer(layerId);
    if (this.map.getLayer(doneLayerId)) this.map.removeLayer(doneLayerId);
    if (this.map.getSource(sourceId)) this.map.removeSource(sourceId);
    if (this.map.getSource(doneSourceId)) this.map.removeSource(doneSourceId);

    this.routeIds.delete(id);
  }

  removeAll(): void {
    for (const id of [...this.routeIds]) {
      this.removeRoute(id);
    }
  }

  dispose(): void {
    this.removeAll();
  }
}
